/**
 * Core Layer Constants
 * Centralized constants used across all applications and layers
 */

import { getConfig } from '../config'

// ===== ENVIRONMENT CONSTANTS =====
export const ENVIRONMENT = {
  DEVELOPMENT: 'development',
  STAGING: 'staging',
  INTEROP: 'interop',
  PRODUCTION: 'production',
} as const

export type Environment = (typeof ENVIRONMENT)[keyof typeof ENVIRONMENT]

// ===== COOKIE CONSTANTS =====
export const COOKIE_NAMES = {
  AUTH: 'catv2',
  SESSION: 'session',
  PREFERENCES: 'preferences',
} as const

// ===== UI CONSTANTS =====
export const BREAKPOINTS = {
  MOBILE: 768,
  TABLET: 1024,
  DESKTOP: 1280,
  LARGE: 1536,
} as const

export const Z_INDEX = {
  DROPDOWN: 1000,
  STICKY: 1020,
  FIXED: 1030,
  MODAL_BACKDROP: 1040,
  MODAL: 1050,
  POPOVER: 1060,
  TOOLTIP: 1070,
  /**
   * Full-screen media lightbox (MediaGalleryModal). Must sit above every app surface it can be
   * launched from - floating chat windows/panel (z-[10010]), their teleported popovers (10011),
   * and app modals such as CPModal / the video upload overlay (~999999) - while staying below
   * TOAST so error/success notifications remain visible over the lightbox.
   */
  MEDIA_GALLERY: 1000000,
  /** Above app modals (e.g. CPModal) and nested overlays (e.g. video upload) so toasts stay visible */
  TOAST: 1010000,
  // Mention-specific layers
  MENTION_POPOVER: 9999,
  MENTION_SUGGESTIONS: 10000,
} as const

/**
 * Use on HLContentWrap: `:notification-config="HIGHRISE_NOTIFICATION_CONFIG"`.
 * Lifts the whole notification Teleport stack above HLModal/CPModal masks (e.g. video upload z-index ~999999).
 * Per-notification `zIndex` from `notification.create()` is not applied by the bundled NotificationEnvironment props.
 */
export const HIGHRISE_NOTIFICATION_CONFIG = {
  containerStyle: {
    zIndex: Z_INDEX.TOAST,
  },
} as const

// ===== THEME CONSTANTS =====
export const THEMES = {
  LIGHT: 'light',
  DARK: 'dark',
  SYSTEM: 'system',
} as const

export const LANGUAGES = {
  EN: 'en',
  ES: 'es',
  FR: 'fr',
  DE: 'de',
  IT: 'it',
  PT: 'pt',
  RU: 'ru',
  ZH: 'zh',
  JA: 'ja',
  KO: 'ko',
} as const

// ===== FILE CONSTANTS =====
export const ALLOWED_FILE_TYPES = [
  'image/svg+xml',
  'image/png',
  'image/webp',
  'image/jpg',
  'image/jpeg',
  'application/pdf',
  'text/plain',
  'application/json',
] as const

export const MAX_FILE_SIZE = {
  IMAGE: 5 * 1024 * 1024, // 5MB
  DOCUMENT: 10 * 1024 * 1024, // 10MB
  VIDEO: 100 * 1024 * 1024, // 100MB
} as const

// ===== ASSET URLS =====
const config = getConfig()

// Base asset URLs
const CLOUD_ASSETS_URL = `${config.FIREBASE.assetsUrl}/assets`
const GOKOLLAB_ASSETS_URL = config.GOKOLLAB_ASSETS_BUCKET_URL

// Common Icons
export const ICONS = {
  // App Icons
  APP_LAUNCHER: `${CLOUD_ASSETS_URL}/app-launcher.svg`,
  HOME: `${CLOUD_ASSETS_URL}/home_05_white.svg`,
  ARROW: `${CLOUD_ASSETS_URL}/arrow_icon.svg`,

  // Social Icons
  FACEBOOK: `${CLOUD_ASSETS_URL}/facebook.svg`,
  INSTAGRAM: `${CLOUD_ASSETS_URL}/instagram.svg`,
  LINKEDIN: `${CLOUD_ASSETS_URL}/linkedin.svg`,
  TWITTER: `${CLOUD_ASSETS_URL}/x-twitter.svg`,
  YOUTUBE: `${CLOUD_ASSETS_URL}/youtube.svg`,

  // Feature Icons
  COMMUNITIES: `${CLOUD_ASSETS_URL}/community_icon.svg`,
  COURSES: `${CLOUD_ASSETS_URL}/courses_icon.svg`,
  AFFILIATES: `${CLOUD_ASSETS_URL}/affiliate_icon.svg`,
  GROUPS: `${CLOUD_ASSETS_URL}/group_branding.svg`,
  TEAM_MEETING: `${CLOUD_ASSETS_URL}/team_meeting.svg`,
  GROUP_BOOK: `${CLOUD_ASSETS_URL}/group_book.svg`,
  COMMUNITIES_PEOPLE: `${CLOUD_ASSETS_URL}/communities_people.svg`,
  GROUPS_CONNECTIONS: `${CLOUD_ASSETS_URL}/groups_connections.svg`,
  COURSES_CAP: `${CLOUD_ASSETS_URL}/courses_cap.svg`,
  MONEY_BAG: `${CLOUD_ASSETS_URL}/money_bag.svg`,

  // UI Icons
  ADD: `${GOKOLLAB_ASSETS_URL}/AddIcon.svg`,
  EXPLORE: `${GOKOLLAB_ASSETS_URL}/Explore.svg`,
  EMPTY_FOLDER: `${CLOUD_ASSETS_URL}/empty_folder.svg`,
  EMPTY_STATE: `${CLOUD_ASSETS_URL}/empty_state.svg`,
  LOADING: `${CLOUD_ASSETS_URL}/NEW.json`,
} as const

/**
 * Card network marks on the shared Firebase CDN (`/assets/*.svg`).
 * Same filenames as clientportal-preview `src/util/const.ts` (Stripe `brand` → icon).
 */
export const PAYMENT_CARD_BRAND_ICONS = {
  VISA: `${CLOUD_ASSETS_URL}/visa.svg`,
  MASTERCARD: `${CLOUD_ASSETS_URL}/mastercard.svg`,
  AMEX: `${CLOUD_ASSETS_URL}/amex.svg`,
  DINERS: `${CLOUD_ASSETS_URL}/diners.svg`,
  DISCOVER: `${CLOUD_ASSETS_URL}/discover.svg`,
  JCB: `${CLOUD_ASSETS_URL}/jcb.svg`,
  UNIONPAY: `${CLOUD_ASSETS_URL}/unionpay.svg`,
  DEFAULT: `${CLOUD_ASSETS_URL}/card.svg`,
} as const

// GoKollab Specific Assets
export const GOKOLLAB_ASSETS = {
  LOGO: {
    LIGHT: `${GOKOLLAB_ASSETS_URL}/gokollab-logo-light-theme-v4.webp`,
    DARK: `${GOKOLLAB_ASSETS_URL}/gokollab-logo-dark-theme-v4.webp`,
    GRADIENT: {
      LIGHT: `${GOKOLLAB_ASSETS_URL}/gokollab-logo-light-theme-v4.webp`,
      DARK: `${GOKOLLAB_ASSETS_URL}/gokollab-logo-dark-theme-v4.webp`,
    },
    TEXT: `${GOKOLLAB_ASSETS_URL}/gokollab-gradient-text-logo-v2.svg`,
    K_LOGO: `${GOKOLLAB_ASSETS_URL}/gokollab-k-logo-v2.svg`,
    K_WITHOUT_BORDER: `${GOKOLLAB_ASSETS_URL}/gokollab-without-border-v4.png`,
    MASTERMIND: `${GOKOLLAB_ASSETS_URL}/gokollab-mastermin-logo-v4.webp`,
    DEFAULT: `${GOKOLLAB_ASSETS_URL}/gokollab-logo-light-theme-v4.webp`,
  },
  HERO: {
    DARK: `${GOKOLLAB_ASSETS_URL}/hero-section-background1.png`,
    LIGHT: `${GOKOLLAB_ASSETS_URL}/hero-section-background2.png`,
  },
  AUTH: {
    LOGIN: `${GOKOLLAB_ASSETS_URL}/login-logo.svg`,
    SIGNUP: `${GOKOLLAB_ASSETS_URL}/sign-up-logo.svg`,
  },
} as const

// Default Images
export const DEFAULT_IMAGES = {
  AVATAR: `${CLOUD_ASSETS_URL}/default_avatar.png`,
  COVER: `${CLOUD_ASSETS_URL}/default_cover.png`,
  GROUP_COVER:
    'https://storage.googleapis.com/highlevel-staging.appspot.com/communities/sTPu59jO68qjLE5h10Ny/groups/02571fec-cf90-4db9-8bce-e0b4a6669c57',
  GROUP_LOGO:
    'https://storage.googleapis.com/highlevel-staging.appspot.com/communities/sTPu59jO68qjLE5h10Ny/groups/255f2156-6614-4f6c-9229-b014960fd2aa',
  NO_POSTS: 'https://storage.googleapis.com/revex-communities-staging/assets/no_posts_icon.svg',
  GROUP_HOME: 'https://staging.cdn.msgsndr.com/assets/communities/community_home.svg',
  CERTIFICATE_THUMBNAIL: 'https://storage.googleapis.com/revex-certificates-production/assets/certificate-thumbnail-landscape.svg',
  NO_CERTIFICATES: 'https://storage.googleapis.com/revex-certificates-production/assets/no-certificates-thumbnail.svg',
  FORCE_UPDATE_BG: 'https://storage.googleapis.com/revex-client-portal-production/assets/force-app-update-bg.png',

  // Communities Default Images
  COMMUNITIES: {
    COVER: `${GOKOLLAB_ASSETS_URL}/default-community-cover.webp`,
    LOGO: `${GOKOLLAB_ASSETS_URL}/default-community-logo.webp`,
    NO_COURSES: `${GOKOLLAB_ASSETS_URL}/no_courses_found.svg`,
    // TODO: figure out correct bucket url config revex-membership-staging bucket
    COURSE_LIST_ICON: `https://storage.googleapis.com/revex-membership-staging/assets/courselist.svg`,
    FIRST_PLACE_MEDAL: 'https://storage.googleapis.com/revex-communities-staging/assets/first_place_badge.png',
    SECOND_PLACE_MEDAL: 'https://storage.googleapis.com/revex-communities-staging/assets/second_place_badge.png',
    THIRD_PLACE_MEDAL: 'https://storage.googleapis.com/revex-communities-staging/assets/third_place_badge.png',
    // Chats empty states
    CHATS_EMPTY_STATE: 'https://storage.googleapis.com/revex-communities-staging/assets/chats-empty-state.webp',
    CHATS_NO_SEARCH_RESULTS: 'https://storage.googleapis.com/revex-communities-staging/assets/chats-no-search-results.webp',
  },

  // Courses Default Images
  COURSES: {
    COVER: 'https://storage.googleapis.com/revex-communities-staging/assets/courses_default_placeholder.svg',
    LOGO: `${CLOUD_ASSETS_URL}/membership/default-course-logo.webp`,
    THUMBNAIL: `${CLOUD_ASSETS_URL}/courses_default_thumbnail.svg`,
    NO_COURSES: `${CLOUD_ASSETS_URL}/no_courses_found.svg`,
  },

  // Events Default Images
  EVENTS: {
    COVER: `https://storage.googleapis.com/revex-client-portal-staging/assets/default-events-cover.webp`,
    // Empty states
    EMPTY_AGENDA: 'https://storage.googleapis.com/revex-client-portal-staging/assets/01-day-1-agenda.svg',
    EMPTY_SPEAKERS: 'https://storage.googleapis.com/revex-client-portal-staging/assets/02-conference-speaker.svg',
    EMPTY_ATTENDEES: 'https://storage.googleapis.com/revex-client-portal-staging/assets/03-attendee-list.svg',
    EMPTY_EXHIBITORS: 'https://storage.googleapis.com/revex-client-portal-staging/assets/04-exhibitor-booth.svg',
    EMPTY_EVENTS: 'https://storage.googleapis.com/revex-client-portal-staging/assets/05-no-events.svg',
    EMPTY_TICKETS: 'https://storage.googleapis.com/revex-client-portal-staging/assets/07-no-tickets.svg',
    EMPTY_CONNECTIONS: 'https://storage.googleapis.com/revex-client-portal-staging/assets/08-no-connections.svg',
    EMPTY_SCHEDULE: 'https://storage.googleapis.com/revex-client-portal-staging/assets/09-empty-schedule.svg',
    EMPTY_BOOKMARKS: 'https://storage.googleapis.com/revex-client-portal-staging/assets/10-no-bookmarks.svg',
  },
} as const

// ===== VALIDATION CONSTANTS =====
export const VALIDATION_RULES = {
  EMAIL_REGEX: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
  PHONE_REGEX: /^\+?[\d\s\-\(\)]+$/,
  PASSWORD_MIN_LENGTH: 8,
  USERNAME_MIN_LENGTH: 3,
  USERNAME_MAX_LENGTH: 20,
} as const

// ===== DATE FORMATS =====
export const DATE_FORMATS = {
  ISO: 'YYYY-MM-DDTHH:mm:ss.sssZ',
  DATE_ONLY: 'YYYY-MM-DD',
  TIME_ONLY: 'HH:mm:ss',
  DATETIME: 'YYYY-MM-DD HH:mm:ss',
  DISPLAY_DATE: 'MMM DD, YYYY',
  DISPLAY_DATETIME: 'MMM DD, YYYY HH:mm',
} as const

// ===== ERROR MESSAGES =====
export const ERROR_MESSAGES = {
  GENERIC: 'Something went wrong!',
  NETWORK_ERROR: 'Network error. Please check your connection.',
  UNAUTHORIZED: 'You are not authorized to perform this action.',
  FORBIDDEN: 'Access denied.',
  NOT_FOUND: 'The requested resource was not found.',
  VALIDATION_ERROR: 'Please check your input and try again.',
  SERVER_ERROR: 'Server error. Please try again later.',
  TIMEOUT_ERROR: 'Request timed out. Please try again.',
} as const

// ===== TEST CONSTANTS =====
export const TEST_DATA = {
  LOCATION_ID: 'hMDUTOU6bIG4hXRzXQKm',
  EMAIL: 'appletestkb@gmail.com',
  BRANDED_EMAIL: 'sakshi.ajmera111@gmail.com',
  TEST_EMAILS: ['appletestkb@gmail.com', 'sakshi.ajmera111@gmail.com', 'appletestapp83@gmail.com'],
} as const

// ===== ANALYTICS CONSTANTS =====
export const ANALYTICS = {
  GTM_IDS: {
    DEFAULT: 'G-91CZG6XDY5',
    GOKOLLAB: 'G-C0S0S6XVMF',
    CP_GOKOLLAB: 'G-VWZEF00Q3B',
  },
  EVENTS: {
    PAGE_VIEW: 'page_view',
    USER_LOGIN: 'user_login',
    USER_LOGOUT: 'user_logout',
    BUTTON_CLICK: 'button_click',
    FORM_SUBMIT: 'form_submit',
    FILE_DOWNLOAD: 'file_download',
  },
} as const

// ===== APP STORE LINKS =====
export const APP_STORE_LINKS = {
  GOKOLLAB: {
    IOS: 'https://apps.apple.com/in/app/gokollab/id6484272411',
    ANDROID: 'https://play.google.com/store/apps/details?id=net.clientclub.app.kollab',
  },
} as const

// ===== EXPORT ALL CONSTANTS =====
export const CONSTANTS = {
  ENVIRONMENT,
  COOKIE_NAMES,
  BREAKPOINTS,
  Z_INDEX,
  HIGHRISE_NOTIFICATION_CONFIG,
  THEMES,
  LANGUAGES,
  ALLOWED_FILE_TYPES,
  MAX_FILE_SIZE,
  ICONS,
  GOKOLLAB_ASSETS,
  DEFAULT_IMAGES,
  VALIDATION_RULES,
  DATE_FORMATS,
  ERROR_MESSAGES,
  TEST_DATA,
  ANALYTICS,
  APP_STORE_LINKS,
} as const

export default CONSTANTS
